import React from 'react';
import {
  PieChart, Pie, Cell,
  LineChart, Line,
  RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import Title from '../../../../utils/title/Title';





export const GenderDonutCharts = ({ data }) => {
  const processData = (location) => {
    const filtered = data.filter(d =>
      location === 'Ghana' ?
        d.country?.includes('Ghana') :
        (!d.country || d.country.includes('Ibadan'))
    );
    const total = filtered.length;
    return [
      { name: `Male: ${filtered.filter(d => d.gender === 'Male').length} (${((filtered.filter(d => d.gender === 'Male').length / total) * 100).toFixed(1)}%)`, value: filtered.filter(d => d.gender === 'Male').length },
      { name: `Female: ${filtered.filter(d => d.gender === 'Female').length} (${((filtered.filter(d => d.gender === 'Female').length / total) * 100).toFixed(1)}%)`, value: filtered.filter(d => d.gender === 'Female').length }
    ];
  };

  const COLORS = ['#89B369', '#eb8145'];

  return (
    <>
      <Title
        text="Gender Distribution Analysis"
        textAlign="center"
        style={{ marginBottom: "1rem", fontSize: "2rem" }}
      />
      <div className="flex flex-row justify-center gap-8">
        {['Ibadan', 'Ghana'].map(location => (
          <div key={location} className="w-1/2">
            <h3 className="text-center text-lg font-semibold">{location}</h3>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={processData(location)}
                  cx="50%"
                  cy="50%"
                  innerRadius={60}
                  outerRadius={80}
                  fill="#8884d8"
                  paddingAngle={5}
                  dataKey="value"
                  label={({ name }) => name}
                >
                  {processData(location).map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        ))}
      </div>
    </>
  );
};

export const AgeLineChart = ({ data }) => {
  const ageRanges = ['18-25', '26-30', '31-35', '36 and above'];

  // Calculate total counts for Ibadan & Ghana
  const totalIbadan = data.filter(d => !d.country || d.country.includes('Ibadan')).length;
  const totalGhana = data.filter(d => d.country?.includes('Ghana')).length;

  const processedData = ageRanges.map(range => {
    const ibadanCount = data.filter(d => (!d.country || d.country.includes('Ibadan')) && d.ageRange === range).length;
    const ghanaCount = data.filter(d => d.country?.includes('Ghana') && d.ageRange === range).length;

    return {
      range,
      Ibadan: ibadanCount,
      Ghana: ghanaCount,
      IbadanLabel: `${ibadanCount} (${((ibadanCount / totalIbadan) * 100).toFixed(1)}%)`,
      GhanaLabel: `${ghanaCount} (${((ghanaCount / totalGhana) * 100).toFixed(1)}%)`
    };
  });

  return (
    <>
      <Title
        text="Age Range Distribution Trends"
        textAlign="center"
        style={{ marginBottom: "1rem", fontSize: "2rem" }}
      />
      <ResponsiveContainer width="100%" height={400}>
        <LineChart
          data={processedData}
          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis dataKey="range" />
          <YAxis />
          <Tooltip
            formatter={(value, name, props) => [`${value} (${((value / (name === "Ibadan" ? totalIbadan : totalGhana)) * 100).toFixed(1)}%)`, name]}
          />
          <Legend
            formatter={(value) => {
              const label = processedData.find(d => d.range === value);
              return label ? `${value}: ${label[value + 'Label']}` : value;
            }}
          />
          <Line type="monotone" dataKey="Ibadan" stroke="#89B369" strokeWidth={2} name="Ibadan" />
          <Line type="monotone" dataKey="Ghana" stroke="#eb8145" strokeWidth={2} name="Ghana" />
        </LineChart>
      </ResponsiveContainer>
    </>
  );
};

export const EmploymentRadarChart = ({ data }) => {
  const statuses = ['Employed', 'Unemployed', 'Freelancing', 'An Entrepreneur', 'Interning'];

  // Calculate total counts for Ibadan & Ghana
  const totalIbadan = data.filter(d => !d.country || d.country.includes('Ibadan')).length;
  const totalGhana = data.filter(d => d.country?.includes('Ghana')).length;

  // Process data for the chart
  const processedData = statuses.map(status => {
    const countIbadan = data.filter(d => (!d.country || d.country.includes('Ibadan')) && d.employmentStatus === status).length;
    const countGhana = data.filter(d => d.country?.includes('Ghana') && d.employmentStatus === status).length;

    return {
      status,
      Ibadan: countIbadan,
      Ghana: countGhana,
      IbadanLabel: `${countIbadan} (${((countIbadan / totalIbadan) * 100).toFixed(1)}%)`,
      GhanaLabel: `${countGhana} (${((countGhana / totalGhana) * 100).toFixed(1)}%)`
    };
  });

  return (
    <>
      <Title
        text="Employment Status Distribution"
        textAlign="center"
        style={{ marginBottom: "1rem", fontSize: "2rem" }}
      />
      <ResponsiveContainer width="100%" height={400}>
        <RadarChart cx="50%" cy="50%" outerRadius="80%" data={processedData}>
          <PolarGrid />
          <PolarAngleAxis dataKey="status" tick={{ fontSize: 12 }} />
          <PolarRadiusAxis />
          <Radar
            name="Ibadan"
            dataKey="Ibadan"
            stroke="#89B369"
            fill="#89B369"
            fillOpacity={0.6}
          />
          <Radar
            name="Ghana"
            dataKey="Ghana"
            stroke="#eb8145"
            fill="#eb8145"
            fillOpacity={0.6}
          />
          <Tooltip formatter={(value, name, props) => props.payload[`${name}Label`]} />
          <Legend />
        </RadarChart>
      </ResponsiveContainer>
    </>
  );
};


export const EducationSunburstChart = ({ data }) => {
  const qualifications = [
    "Graduate",
    "Undergraduate",
    "SSCE Holder",
    "Other(specify)",
  ];

  // Process data for Sunburst structure
  const processData = (location) => {
    const filtered = data.filter((d) =>
      location === "Ghana"
        ? d.country?.includes("Ghana")
        : !d.country || d.country.includes("Ibadan")
    );

    const total = filtered.length;

    return qualifications.map((qual) => {
      const count = filtered.filter(
        (d) => d.educationalQualification === qual
      ).length;
      return {
        name: `${qual}: ${count}  (${((count / total) * 100).toFixed(1)}%)`,
        value: count,
      };
    });
  };

  const COLORS = ["#89B369", "#eb8145", "#8dd1e1", "#727272"];

  return (
    <>
      <h2 className="text-center text-2xl font-semibold">
        Education Qualification Distribution
      </h2>
      <div className="flex flex-row justify-center gap-8">
        {["Ibadan", "Ghana"].map((location, index) => (
          <div key={index} className="w-1/2">
            <h3 className="text-center text-lg font-semibold">{location}</h3>
            <ResponsiveContainer width="100%" height={400}>
              <PieChart>
                {/* Outer Ring - Qualification Levels */}
                <Pie
                  data={processData(location)}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="50%"
                  outerRadius={120}
                  fill="#8884d8"
                  label={({ name }) => name}
                >
                  {processData(location).map((entry, index) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={COLORS[index % COLORS.length]}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        ))}
      </div>
    </>
  );
};

export default EducationSunburstChart;